// @ts-nocheck
import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  Image,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  BookOpen,
  ChevronLeft,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { Course, Mentor } from '@/types/mentor';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonCourseCard } from '@/components/SkeletonLoader';

export default function MentorCoursesScreen() {
  const router = useRouter();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCourses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        router.replace('/mentor/login');
        return;
      }

      const { data: coursesData, error } = await supabase
        .from('processed_raw_courses')
        .select('course_id, title, summary, price, format, level, audience, images')
        .eq('mentor_email', user.email);

      if (error) {
        console.error('Error loading courses:', error);
      }

      setCourses(coursesData || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);


  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <Text style={styles.title}>הקורסים שלי</Text>
            <Pressable onPress={() => router.back()} style={styles.backButton}>
              <ChevronLeft color={theme.colors.text} size={24} />
            </Pressable>
          </View>
        </View>
        <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
          <SkeletonCourseCard />
          <SkeletonCourseCard />
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text
            style={styles.title}
            accessible={true}
            accessibilityRole="header"
            {...(Platform.OS === 'web' && {
              role: 'heading',
              'aria-level': 1,
            } as any)}
          >
            הקורסים שלי
          </Text>
          <Pressable
            onPress={() => router.back()}
            style={styles.backButton}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="חזור לדאשבורד"
            {...(Platform.OS === 'web' && {
              'aria-label': 'חזור לדאשבורד',
            } as any)}
          >
            <ChevronLeft color={theme.colors.text} size={24} aria-hidden={true} />
          </Pressable>
        </View>
        <Text style={styles.subtitle}>
          {courses.length} {courses.length === 1 ? 'קורס' : 'קורסים'}
        </Text>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {courses.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="עדיין לא משויכים קורסים לחשבון זה"
            description="הקורסים שלך יופיעו כאן לאחר שהם ישויכו לאימייל שלך במערכת"
          />
        ) : (
          courses.map((course: any) => {
            return (
              <View key={course.course_id} style={styles.courseCard}>
                {course.images && (
                  <Image
                    source={{ uri: course.images }}
                    style={styles.courseImage}
                    resizeMode="cover"
                    accessible={true}
                    accessibilityLabel={`תמונת הקורס ${course.title}`}
                  />
                )}

                <View style={styles.courseContent}>
                  <View style={styles.courseHeader}>
                    <Text style={styles.courseName} numberOfLines={2}>
                      {course.title}
                    </Text>
                  </View>

                  {course.summary && (
                    <Text style={styles.courseDescription} numberOfLines={3}>
                      {course.summary}
                    </Text>
                  )}

                  <View style={styles.courseDetails}>
                    {course.price && (
                      <View style={styles.detailItem}>
                        <Text style={styles.detailLabel}>מחיר:</Text>
                        <Text style={styles.detailValue}>{course.price}</Text>
                      </View>
                    )}
                    {course.format && (
                      <View style={styles.detailItem}>
                        <Text style={styles.detailLabel}>פורמט:</Text>
                        <Text style={styles.detailValue}>{course.format}</Text>
                      </View>
                    )}
                    {course.level && (
                      <View style={styles.detailItem}>
                        <Text style={styles.detailLabel}>רמה:</Text>
                        <Text style={styles.detailValue}>{course.level}</Text>
                      </View>
                    )}
                    {course.audience && (
                      <View style={styles.detailItem}>
                        <Text style={styles.detailLabel}>קהל יעד:</Text>
                        <Text style={styles.detailValue}>{course.audience}</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            );
          })
        )}
      </ScrollView>

      <AccessibilityButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  header: {
    backgroundColor: theme.colors.backgroundLight,
    paddingHorizontal: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    gap: theme.spacing.xs,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 24,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    paddingBottom: 100,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.xl * 2,
    gap: theme.spacing.md,
  },
  emptyStateText: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.textLight,
  },
  emptyStateSubtext: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    maxWidth: 280,
  },
  courseCard: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  courseImage: {
    width: '100%',
    height: 180,
    backgroundColor: theme.colors.background,
  },
  courseContent: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  courseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
  courseName: {
    flex: 1,
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    lineHeight: 28,
  },
  courseDescription: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    lineHeight: 22,
  },
  courseDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  detailItem: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  detailLabel: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  detailValue: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
  },
});
